import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { getAllLoginLogsURL } from "../libraries/apiUrl";
import { _get } from "../libraries/fetch";

const TABLE_HEADINGS = [
  "Sr No.",
  "Created Date",
  "Name",
  "Email",
  "Contact",
  "City",
  "Gym Name",
  "IP Address",
  "IP Location"
];

export function LoginLogsSheet() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchLoginLogs = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await _get(getAllLoginLogsURL(), { useCache: false });

      if (response == null) {
        throw new Error("Failed to load login logs.");
      }

      if (response?.success === false || response?.status === false) {
        throw new Error(response.message || response.error || "Failed to load login logs.");
      }

      const nextItems = extractLoginLogItems(response)
        .map(normalizeLoginLogItem)
        .sort((a, b) => getSortTime(b.createdDate) - getSortTime(a.createdDate));

      setItems(nextItems);
    } catch (err) {
      setError(err?.message || "Failed to load login logs. Please try again.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLoginLogs();
  }, [fetchLoginLogs]);

  const rows = useMemo(() => items, [items]);

  return (
    <>
      <div style={actionsStyle}>
        <button type="button" onClick={fetchLoginLogs} disabled={loading} style={refreshButtonStyle}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>
      <div style={tableShellStyle}>
        {loading ? (
          <div style={emptyStyle}>Loading login logs...</div>
        ) : error ? (
          <div style={errorStyle}>{error}</div>
        ) : rows.length === 0 ? (
          <div style={emptyStyle}>No login logs found.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr style={tableHeadRowStyle}>
                  {TABLE_HEADINGS.map((heading) => (
                    <th key={heading} style={tableHeaderStyle}>
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((item, index) => (
                  <tr
                    key={item.id || `${item.email}-${item.createdDate}-${index}`}
                    style={{
                      borderBottom: "1px solid #333",
                      backgroundColor: index % 2 === 0 ? "#1a1d23" : "#20232b"
                    }}
                  >
                    <td style={tableCellStyle}>{index + 1}</td>
                    <td style={tableCellStyle}>{formatDate(item.createdDate)}</td>
                    <td style={tableCellStyle}>{item.name}</td>
                    <td style={tableCellStyle}>{item.email}</td>
                    <td style={tableCellStyle}>{item.contact}</td>
                    <td style={tableCellStyle}>{item.city}</td>
                    <td style={tableCellStyle}>{item.gymName}</td>
                    <td style={tableCellStyle}>{item.ipAddress}</td>
                    <td style={messageCellStyle}>{item.ipLocation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function LoginLogsPage() {
  return <Navigate to="/database-sheets?tab=login-logs" replace />;
}

function extractLoginLogItems(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (!response || typeof response !== "object") {
    return [];
  }

  const listKeys = [
    "data",
    "loginLogs",
    "loginlogs",
    "login-logs",
    "logs",
    "response",
    "result",
    "results",
    "records",
    "items",
    "docs"
  ];

  for (const key of listKeys) {
    const value = response[key];

    if (Array.isArray(value)) {
      return value;
    }

    const nestedItems = extractLoginLogItems(value);
    if (nestedItems.length > 0) {
      return nestedItems;
    }
  }

  return [];
}

function normalizeLoginLogItem(item) {
  return {
    id: getValue(item, ["_id", "id"]),
    createdDate: getValue(item, ["createdDate", "createdAt", "created_at", "loginDate", "date"]),
    name: formatValue(getValue(item, ["name", "fullName", "userName"])),
    email: formatValue(getValue(item, ["email", "loginMail", "loginId"])),
    contact: formatValue(getValue(item, ["contact", "phone", "phoneNumber", "mobile"])),
    city: formatValue(getValue(item, ["city", "City"])),
    gymName: formatValue(getValue(item, ["gymName", "organization", "branchName"])),
    ipAddress: formatValue(getValue(item, ["ipAddress", "ip", "IP", "clientIp"])),
    ipLocation: formatIpLocation(getValue(item, ["ipLocation", "location"]))
  };
}

function formatIpLocation(value) {
  if (value === undefined || value === null || value === "") {
    return "N/A";
  }

  if (Array.isArray(value)) {
    const parts = value
      .map((entry) => {
        if (!entry || typeof entry !== "object") {
          return formatValue(entry);
        }
        return [entry.city, entry.country].filter(Boolean).join(", ");
      })
      .filter((part) => part && part !== "N/A");

    return parts.length > 0 ? parts.join(" | ") : "N/A";
  }

  if (typeof value === "object") {
    const joined = [value.city, value.country].filter(Boolean).join(", ");
    return joined || formatValue(value);
  }

  return formatValue(value);
}

function getValue(source, keys) {
  if (!source || typeof source !== "object") {
    return "";
  }

  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null && source[key] !== "") {
      return source[key];
    }
  }

  return "";
}

function formatValue(value) {
  if (value === undefined || value === null || value === "") {
    return "N/A";
  }

  if (Array.isArray(value)) {
    return value.map(formatValue).join(", ");
  }

  if (typeof value === "object") {
    return value.name || value.title || value.label || value._id || value.id || JSON.stringify(value);
  }

  return String(value);
}

function getSortTime(dateString) {
  if (!dateString || dateString === "N/A") {
    return 0;
  }

  const time = new Date(dateString).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function formatDate(dateString) {
  if (!dateString || dateString === "N/A") {
    return "N/A";
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return formatValue(dateString);
  }

  return date.toLocaleString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

const actionsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  marginBottom: "16px"
};

const refreshButtonStyle = {
  padding: "10px 18px",
  fontSize: "14px",
  fontWeight: 600,
  backgroundColor: "#187718",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer"
};

const tableShellStyle = {
  overflowX: "auto",
  border: "1px solid #3a3f4b",
  borderRadius: "10px",
  backgroundColor: "#1a1d23",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
};

const tableStyle = {
  width: "100%",
  minWidth: "1080px",
  borderCollapse: "collapse",
  fontSize: "14px"
};

const tableHeadRowStyle = {
  backgroundColor: "#102235",
  borderBottom: "2px solid #187718"
};

const tableHeaderStyle = {
  padding: "14px 12px",
  color: "#fff",
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "0.3px",
  textAlign: "left",
  whiteSpace: "nowrap"
};

const tableCellStyle = {
  padding: "12px",
  color: "#fff",
  verticalAlign: "middle"
};

const messageCellStyle = {
  ...tableCellStyle,
  minWidth: "160px",
  lineHeight: 1.35
};

const emptyStyle = {
  padding: "42px 20px",
  color: "#9ca3af",
  textAlign: "center"
};

const errorStyle = {
  padding: "18px",
  margin: "20px",
  borderRadius: "8px",
  backgroundColor: "rgba(213, 23, 23, 0.15)",
  border: "1px solid rgba(213, 23, 23, 0.45)",
  color: "#ff9b9b",
  fontWeight: 600
};

export default LoginLogsPage;
